/**
 * The scanner components' single entry point for QR signature checks.
 *
 * Verifies a code that has already passed validateBarcode, decides what the
 * mode says to do with the result, raises the app-wide notice, and remembers
 * the scan briefly so a flow that navigates before posting (KeyDistribution ->
 * DistributeKey) can still send its token.
 */
import { splitScannedCode } from '@/utils/scanUtils.js'
import { QR_MODE, QR_VERIFICATION, qrScanDecision } from '@/utils/qrVerify.js'
import { qrNoticeFor } from '@/utils/qrMessages.js'
import { showQrNotice } from '@/composables/useQrNotice.js'
import { inspectScannedCode } from '@/services/qrKeyCache.js'

const REMEMBER_MS = 5 * 60 * 1000
const remembered = new Map()

/**
 * @param {string} raw the full scanned string
 * @returns {Promise<{scan: {raw, reference, token, verification, mode}, decision: {block, warn, verified}}>}
 */
export async function checkScannedCode(raw) {
  let scan
  try {
    scan = await inspectScannedCode(raw)
  } catch {
    // The cache itself failed. Nothing here says the ticket is bad, so never
    // block on it; the server re-verifies.
    const { reference, token } = splitScannedCode(raw)
    scan = {
      raw,
      reference,
      token,
      verification: token ? QR_VERIFICATION.UNKNOWN_KEY : QR_VERIFICATION.UNSIGNED,
      mode: QR_MODE.WARN,
    }
  }
  const decision = qrScanDecision(scan.verification, scan.mode)
  showQrNotice(qrNoticeFor(scan.verification, decision))
  if (!decision.block) rememberScan(scan)
  return { scan, decision }
}

export function rememberScan(scan) {
  if (!scan?.reference) return
  remembered.set(scan.reference, { scan, at: Date.now() })
  for (const [ref, entry] of remembered) {
    if (Date.now() - entry.at > REMEMBER_MS) remembered.delete(ref)
  }
}

/** The recent scan of this reference, or null. */
export function recallScan(reference) {
  const entry = remembered.get(reference)
  if (!entry) return null
  if (Date.now() - entry.at > REMEMBER_MS) {
    remembered.delete(reference)
    return null
  }
  return entry.scan
}
